import { useEffect, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import '../styles/landing.css';

const CONTACT_EMAIL = 'support@agent-mesh.org';
const LAST_UPDATED = 'July 1, 2026';

/** Shared marketing-styled shell for legal / contact pages. */
function LegalLayout({ title, children }: { title: string; children: ReactNode }) {
  useEffect(() => {
    const prev = document.title;
    document.title = `${title} — AgentMesh`;
    return () => {
      document.title = prev;
    };
  }, [title]);

  return (
    <div className="lp min-h-screen">
      <header className="border-b border-[var(--line)] bg-[var(--paper)]">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
          <Link to="/" className="lp-display text-lg">
            ← AgentMesh
          </Link>
          <Link to="/register" className="lp-btn lp-btn-primary">
            Start free
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-5 py-14">
        <h1 className="lp-display text-4xl">{title}</h1>
        <p className="lp-mono mt-3 text-xs text-[var(--ink-soft)]">
          Last updated: {LAST_UPDATED}
        </p>
        <div className="lp-prose mt-8 space-y-5 text-[var(--ink-soft)]">{children}</div>
      </main>
      <footer className="border-t border-[var(--line)] bg-[var(--paper-2)]">
        <div className="mx-auto flex max-w-3xl flex-wrap gap-x-6 gap-y-2 px-5 py-6 text-sm text-[var(--ink-soft)]">
          <Link to="/" className="hover:text-[var(--ink)]">Home</Link>
          <Link to="/terms" className="hover:text-[var(--ink)]">Terms</Link>
          <Link to="/privacy" className="hover:text-[var(--ink)]">Privacy</Link>
          <Link to="/refund" className="hover:text-[var(--ink)]">Refund policy</Link>
          <Link to="/contact" className="hover:text-[var(--ink)]">Contact</Link>
        </div>
      </footer>
    </div>
  );
}

function H2({ children }: { children: ReactNode }) {
  return <h2 className="lp-display pt-4 text-xl text-[var(--ink)]">{children}</h2>;
}

// ── Terms ──────────────────────────────────────────────────────

export function Terms() {
  return (
    <LegalLayout title="Terms of Service">
      <p>
        These Terms govern your use of AgentMesh (the “Service”), an AI multi-agent
        orchestration platform. By creating an account or using the Service, you agree
        to these Terms.
      </p>
      <H2>1. Your account</H2>
      <p>
        You must provide accurate information and are responsible for activity under
        your account and for keeping your credentials secure. You must be at least 18
        years old, or the age of majority in your jurisdiction.
      </p>
      <H2>2. Acceptable use</H2>
      <p>
        You agree not to misuse the Service, including: violating any law; infringing
        others’ rights; attempting to breach security or access other tenants’ data;
        or using the Service to generate unlawful, harmful, or abusive content. We may
        suspend accounts that violate these Terms.
      </p>
      <H2>3. Your content</H2>
      <p>
        You retain ownership of the agents, workflows, prompts, and data you create
        (“Your Content”). You grant us a limited licence to host and process Your
        Content solely to operate the Service. You are responsible for having the
        rights to any data you submit.
      </p>
      <H2>4. Subscriptions &amp; payment</H2>
      <p>
        Paid plans are billed through our reseller and Merchant of Record, Paddle.com,
        which handles payment and invoicing. Subscriptions renew automatically until
        cancelled. See our <Link to="/refund" className="underline">Refund Policy</Link>.
      </p>
      <H2>5. Availability &amp; changes</H2>
      <p>
        We aim for high availability but do not guarantee uninterrupted service. We may
        modify or discontinue features with reasonable notice.
      </p>
      <H2>6. Disclaimers &amp; liability</H2>
      <p>
        The Service is provided “as is”. AI outputs may be inaccurate; you are
        responsible for reviewing agent results before acting on them. To the maximum
        extent permitted by law, our liability is limited to the amount you paid in the
        12 months preceding the claim.
      </p>
      <H2>7. Contact</H2>
      <p>
        Questions about these Terms? Email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>.
      </p>
    </LegalLayout>
  );
}

// ── Privacy ────────────────────────────────────────────────────

export function Privacy() {
  return (
    <LegalLayout title="Privacy Policy">
      <p>
        This Policy explains what personal data AgentMesh collects and how we use it.
        We aim to collect the minimum necessary to run the Service.
      </p>
      <H2>Data we collect</H2>
      <p>
        <strong>Account data:</strong> name and email you provide at signup.{' '}
        <strong>Usage data:</strong> agents, workflows, and execution logs you create.{' '}
        <strong>Payment data:</strong> handled entirely by Paddle; we never see or store
        full card details.
      </p>
      <H2>How we use it</H2>
      <p>
        To provide and secure the Service, process your workflows, communicate with you
        about your account, and comply with legal obligations. We do not sell your
        personal data.
      </p>
      <H2>AI processing</H2>
      <p>
        When your agents run, the inputs you route to them are sent to the configured
        large-language-model provider (e.g. Anthropic, OpenAI) to generate results. We
        forward only the fields your workflow declares.
      </p>
      <H2>Sub-processors</H2>
      <p>
        We rely on infrastructure and service providers including our hosting provider,
        our email provider, and Paddle for payments. Each processes data only to deliver
        their part of the Service.
      </p>
      <H2>Your rights</H2>
      <p>
        You may access, correct, export, or delete your personal data. To make a request,
        email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>.
        You can delete your account at any time.
      </p>
      <H2>Retention &amp; security</H2>
      <p>
        We keep data for as long as your account is active and apply workspace
        isolation, encryption in transit, signed authentication, and rate limiting to
        protect it.
      </p>
    </LegalLayout>
  );
}

// ── Refund ─────────────────────────────────────────────────────

export function Refund() {
  return (
    <LegalLayout title="Refund &amp; Cancellation Policy">
      <H2>Cancelling</H2>
      <p>
        You can cancel your Pro subscription at any time from your account. Cancellation
        stops the next renewal; you keep Pro access until the end of the current billing
        period. There is a free plan you can always fall back to.
      </p>
      <H2>Refunds</H2>
      <p>
        If you are not satisfied, contact us within <strong>14 days</strong> of a charge
        and we will refund that payment. Because a free plan lets you evaluate the
        Service before paying, refunds outside this window are considered case-by-case.
      </p>
      <H2>How billing works</H2>
      <p>
        Payments and refunds are processed by our Merchant of Record, Paddle.com.
        Approved refunds are returned to your original payment method; timing depends on
        your bank or card issuer.
      </p>
      <H2>Requesting a refund</H2>
      <p>
        Email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`} className="underline">{CONTACT_EMAIL}</a>{' '}
        with the email on your account and we’ll take care of it.
      </p>
    </LegalLayout>
  );
}

// ── Contact ────────────────────────────────────────────────────

export function About() {
  return (
    <LegalLayout title="About AgentMesh">
      <p>
        AgentMesh is a no-code platform for building teams of AI agents that take real
        actions — call your APIs, send email and Slack, read and write your calendar — and
        run on their own, on a schedule or when an outside system fires a webhook.
      </p>

      <H2>Who builds it</H2>
      <p>
        AgentMesh is built and operated by{' '}
        <strong className="text-[var(--ink)]">Aghassi Sargsyan</strong>, an independent
        software engineer and entrepreneur. It is not a venture-backed company — it is one
        engineer shipping a product, in the open, to production.
      </p>
      <p>
        <a
          href="https://www.linkedin.com/in/aghassis/"
          target="_blank"
          rel="noopener noreferrer me"
          className="underline text-[var(--signal)]"
        >
          LinkedIn
        </a>
        {' · '}
        <a href={`mailto:${CONTACT_EMAIL}`} className="underline text-[var(--signal)]">
          {CONTACT_EMAIL}
        </a>
      </p>

      <H2>What’s actually built</H2>
      <p>
        We try to describe the product as it is, not as we wish it were. Today AgentMesh
        really does: run agents that call <strong className="text-[var(--ink)]">ten tools</strong>{' '}
        (HTTP requests to any REST API, email, Slack, Discord, Telegram, Google Chat, Notion,
        outbound webhooks, and Google Calendar); orchestrate them across{' '}
        <strong className="text-[var(--ink)]">six execution modes</strong> (sequential,
        parallel, conditional, fan-out, loop, and dependency graphs); and run unattended via
        cron schedules and inbound webhook triggers. Every run is logged and replayable.
      </p>
      <p>
        On security: tenant isolation, JWT auth, rate limiting, encryption at rest for your
        integration and LLM keys, and an SSRF guard on every outbound tool call. We are{' '}
        <strong className="text-[var(--ink)]">GDPR-aware but not SOC 2 or HIPAA certified</strong>
        , and we say so rather than implying otherwise.
      </p>

      <H2>Not to be confused with</H2>
      <p>
        “Agent mesh” is also a generic architectural term, and several unrelated projects and
        packages share the name. This AgentMesh is the product at{' '}
        <strong className="text-[var(--ink)]">agent-mesh.org</strong> — it is not affiliated
        with, endorsed by, or derived from any other project using the AgentMesh or “agent
        mesh” name.
      </p>

      <H2>Get in touch</H2>
      <p>
        Questions, bugs, or feature ideas:{' '}
        <a href={`mailto:${CONTACT_EMAIL}`} className="underline text-[var(--signal)]">
          {CONTACT_EMAIL}
        </a>
        . See also the{' '}
        <Link to="/docs" className="underline text-[var(--signal)]">
          developer docs
        </Link>{' '}
        and the{' '}
        <Link to="/help" className="underline text-[var(--signal)]">
          help center
        </Link>
        .
      </p>
    </LegalLayout>
  );
}

export function Contact() {
  return (
    <LegalLayout title="Contact us">
      <p>
        We’d love to hear from you — whether it’s a question, a bug, a feature idea, or
        help getting your first AI team running.
      </p>
      <H2>Email</H2>
      <p>
        <a href={`mailto:${CONTACT_EMAIL}`} className="underline text-[var(--signal)]">
          {CONTACT_EMAIL}
        </a>
        <br />
        We aim to reply within one business day.
      </p>
      <H2>Support &amp; billing</H2>
      <p>
        For billing questions, include the email address on your account. Payments are
        handled by Paddle.com, our Merchant of Record.
      </p>
      <H2>Ready to try it?</H2>
      <p>
        <Link to="/register" className="lp-btn lp-btn-primary mt-2">
          Start free
        </Link>
      </p>
    </LegalLayout>
  );
}
